<script setup lang="ts">
	import compareObjects from '@/helpers/compareObjects'
	import { useQueryClient } from '@tanstack/vue-query'
	import { objectPick } from '@vueuse/core/index.mjs'
	import { cloneDeep, groupBy } from 'lodash'
	import { inject, ref, watch, watchEffect } from 'vue'
	import { useToast } from 'vue-toastification'

	import HasPermission from '@/components/HasPermission.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import FieldSet from '@/modules/companies/components/FieldSet.vue'
	import MarkupInput from '@/modules/companies/components/edit/MarkupInput.vue'
	import useCompanyMarkup from '@/modules/companies/composables/useCompanyMarkup'
	import useCompanyServiceCharge from '@/modules/companies/composables/useCompanyServiceCharge'
	import { CompanyEditTab } from '@/modules/companies/interfaces/companyEditTab'

	interface Props extends CompanyEditTab {}

	interface Emit {
		(event: 'invalidate-data', value: any): void
	}

	const props = defineProps<Props>()
	const emit = defineEmits<Emit>()

	const toast = useToast()

	const queryClient = useQueryClient()

	const { hasPermissionUser } = useHasPermissionUser()

	const {
		data: markups,
		isLoading: markupsLoading,
		updateCompanyMarkupAsync,
	} = useCompanyMarkup({
		params: {
			companyId: props.company.id,
			id: props.company.id,
		},
	})

	const {
		data: serviceCharges,
		isLoading: serviceChargesLoading,
		updateCompanyServiceChargeAsync,
		updateCompanyServiceChargeLoading,
	} = useCompanyServiceCharge({
		params: {
			companyId: props.company.id,
			id: props.company.id,
		},
	})

	const invalidateQueryData = async () => {
		await queryClient.invalidateQueries([
			'companyServiceCharge',
			props.company.id,
		])
		await queryClient.invalidateQueries(['companyMarkup', props.company.id])
	}

	const negotiationsFields = ref(
		serviceCharges.value ?
			groupBy(
				[...serviceCharges.value].map((serviceCharge) => ({
					...objectPick(serviceCharge, [
						'id',
						'amount',
						'companyId',
						'type',
						'serviceId',
						'displayName',
					]),
					serviceName: serviceCharge.serviceName,
				}))
			)
		:	{}
	)

	const staticNegotiationsFields = cloneDeep(negotiationsFields.value)

	const markupsFields = ref(
		markups.value ?
			groupBy(
				markups.value?.map((markup) => ({
					...objectPick(markup, [
						'id',
						'value',
						'type',
						'agencyType',
						'agencyValue',
						'serviceId',
						'displayName',
					]),
					serviceName: markup.serviceName,
				})),
				({ serviceName }) => serviceName
			)
		:	{}
	)

	const staticMarkupsFields = cloneDeep(markupsFields.value)

	const fieldInfo = {
		national: ['🇲🇽:', 'ej. 235.5', 'national'],
		international: ['🌎:', 'ej. 435.5', 'international'],
	}

	type fieldInfoKey = keyof typeof fieldInfo

	watchEffect(() => {
		if (serviceCharges.value) {
			negotiationsFields.value = groupBy(
				[...serviceCharges.value].map((serviceCharge) => ({
					...objectPick(serviceCharge, [
						'id',
						'amount',
						'companyId',
						'type',
						'serviceId',
						'displayName',
					]),
					serviceName: serviceCharge.displayName,
				})),
				({ displayName }) => displayName
			)
		}

		if (markups.value) {
			markupsFields.value = groupBy(
				markups.value?.map((markup) => ({
					...objectPick(markup, [
						'id',
						'value',
						'type',
						'agencyType',
						'agencyValue',
						'serviceId',
						'displayName',
					]),
					serviceName: markup.serviceName,
				})),
				({ serviceName }) => serviceName
			)
		}
	})

	const handleSave = async () => {
		let hasValidationError = false
		let changesDetected = false

		const updateNegotiationsRequests = await Promise.all(
			Object.values(negotiationsFields.value)
				.flat()
				.map(async (entry) => {
					if (
						!compareObjects(
							entry,
							serviceCharges.value?.find((field) => field.id === entry.id) ?? {}
						)
					) {
						changesDetected = true // Se detectó un cambio
						let maxAmount = 0
						if (entry.type === 'national') {
							maxAmount = 500
						} else if (entry.type === 'international') {
							maxAmount = 600
						}

						if (Number(entry.amount) > maxAmount) {
							const errorMessage = `El cargo por servicio para ${entry.serviceName} (${entry.type}) excede el límite máximo de $${maxAmount}`
							toast.error(errorMessage)
							hasValidationError = true
							return Promise.reject(new Error(errorMessage)) // Rechazar la promesa en caso de error
						}
						if (!entry.amount) {
							entry.amount = 0

							const errorMessage = `El cargo por servicio para ${entry.serviceName} (${entry.type}) no puede ser menor a 0`
							toast.error(errorMessage)
							hasValidationError = true
							return Promise.reject(new Error(errorMessage))
						} else {
							return updateCompanyServiceChargeAsync({
								params: {
									...entry,
									id: entry.id as number,
								},
							})
						}
					}
				})
		)

		const updateMarkupsRequests = Object.values(markupsFields.value)
			.flat()
			.map((entry) => {
				if (
					!compareObjects(
						entry,
						markups.value?.find((field) => field.id === entry.id) ?? {}
					)
				) {
					changesDetected = true // Se detectó un cambio
					return updateCompanyMarkupAsync({
						params: {
							...entry,
							companyId: props.company.id,
							id: entry.id as number,
						},
					})
				}
				return null
			})
			.filter((request) => request !== null)

		try {
			const updateRequests = [
				...updateNegotiationsRequests,
				...updateMarkupsRequests,
			]

			if (updateRequests.length) {
				await Promise.all(updateRequests)

				if (changesDetected) {
					toast.success('Se han guardado los cambios')
				} else {
					toast.info('No se detectaron cambios para guardar')
				}
			}
		} catch (error) {
			if (!hasValidationError) {
				toast.error('Ha ocurrido un error al guardar los cambios')
				console.log(error)
			}
		} finally {
			await invalidateQueryData()
		}
	}

	const handleBlur = (e: FocusEvent) => {
		if (e.target instanceof HTMLInputElement) {
			if (e.target.value === '') {
				e.target.value = '0'
			}
		}
	}
</script>

<template>
	<RoundedBox class="bg-white p-10">
		<section class="w-full grid gap-[1.563rem]">
			<div class="flex flex-col gap-2">
				<h2 class="text-xl font-medium">Negociaciones</h2>
			</div>

			<HasPermission
				:name-permission="'viewAny'"
				:entity-type="'App\\Models\\Markup'"
			>
				<FieldSet>
					<template v-for="service in Object.keys(markupsFields)">
						<MarkupInput
							v-for="(field, index) in markupsFields[service]"
							:key="field.id"
							@focus-out="handleBlur"
							v-model="markupsFields[service][index]"
							:show-agency="field.serviceName === 'Hotel'"
							:label="`${field.displayName}  ${field.serviceName === 'Hotel' || field.serviceName === 'Car' ? 'por noche' : ''}:`"
						/>
					</template>
				</FieldSet>
			</HasPermission>

			<div class="flex flex-col gap-2">
				<h2
					v-if="
						serviceCharges?.length &&
						hasPermissionUser('viewAny', 'App\\Models\\ServiceCharge')
					"
					class="text-xl font-medium"
				>
					Cargo por servicios
				</h2>

				<p>Se cobra por el total de servicio completo</p>
			</div>

			<HasPermission
				:name-permission="'viewAny'"
				:entity-type="'App\\Models\\ServiceCharge'"
			>
				<template v-if="serviceCharges?.length">
					<div
						class="grid grid-cols-1 md:grid-cols-2 gap-[3.875rem] gap-y-[1.563rem]"
					>
						<fieldset
							v-for="serviceKey in Object.keys(negotiationsFields)"
							class="grid gap-2"
							:key="serviceKey"
						>
							<h3 class="text-lg font-medium">
								{{ serviceKey }}
							</h3>

							<FieldSet class="!gap-[2.25rem] sm:!grid-cols-2">
								<StyledInput
									type="number"
									v-for="(field, index) in negotiationsFields[serviceKey]"
									@focus-out="handleBlur"
									v-model="negotiationsFields[serviceKey][index].amount"
									:label="fieldInfo[field.type as fieldInfoKey][0]"
									:placeholder="fieldInfo[field.type as fieldInfoKey][2]"
									:max="
										fieldInfo[field.type as fieldInfoKey][0] == '🇲🇽:' ?
											500
										:	600
									"
									:key="field.id"
									class="!flex-row items-center"
									@update:model-value="
										() => {
											if (
												negotiationsFields[serviceKey][index].amount == null
											) {
												negotiationsFields[serviceKey][index].amount = 0
											}
										}
									"
								/>
							</FieldSet>
						</fieldset>
					</div>
				</template>
			</HasPermission>

			<div v-if="!markupsFields.length && serviceCharges?.length">
				<StyledButton
					v-if="
						hasPermissionUser('update', 'App\\Models\\Markup') ||
						hasPermissionUser('update', 'App\\Models\\ServiceCharge')
					"
					label="Guardar"
					class="bg-[#253C70] text-white ml-auto"
					@click="handleSave"
					:loading="updateCompanyServiceChargeLoading"
					:disabled="updateCompanyServiceChargeLoading"
				/>
			</div>
		</section>

		<LoadingModal :when="serviceChargesLoading || markupsLoading" />
	</RoundedBox>
</template>

<style scoped></style>
